import { createContext, useContext, useReducer, useCallback } from 'react';
import { DEFAULT_VISIBLE_COLUMNS, resolveVisibleColumns } from './inventoryColumns';
// VIEW_MODES / SORT_KEYS moved to a leaf module to break a circular
// dep (useInventoryData → InventoryContext → inventoryColumns →
// useInventoryData). Re-exported here so existing import sites work.
import { VIEW_MODES, SORT_KEYS, UNITS } from './inventoryConstants';
export { VIEW_MODES, SORT_KEYS, UNITS };

/**
 * InventoryContext — state management for the document inventory.
 *
 * Centralizes: selection set, filters, sort, view mode, expanded docs,
 * active bulk action, and folder navigation.
 *
 * Designed for extensibility — new filter types or view modes only
 * require adding a reducer case and a UI control.
 */

const InventoryContext = createContext();

// ── Reducer ──────────────────────────────────────────────────

const initialState = {
  // Folder
  currentFolder: '__all__',

  // Filters — compositional chip rail model.
  // Each chip: { axis: <FILTER_AXES key>, value: string }
  // Invariant (v1): single value per axis; ADD_FILTER replaces existing
  // chip on the same axis. Multi-value within an axis is the obvious v2.
  searchText: '',
  filters: [],
  duplicateFilter: false,

  // Sort
  sortKey: SORT_KEYS.FILENAME,
  sortAsc: true,

  // View
  viewMode: VIEW_MODES.LIST,
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  // Column widths a person dragged, by column id (px); absent = the registry's default.
  columnWidths: {},

  // Unit (plan §3.4c): 'files' | 'documents' once the user chose; null = automatic
  // (documents when the case has a cut file, files otherwise — DocumentInventory resolves it).
  unit: null,
  // Documents unit only: also show the files that were cut into documents, as rows of their own.
  showSourceFiles: false,

  // Selection
  selected: new Set(),
  bulkAction: null, // 'move' | 'delete' | 'reprocess' | 'scope' | null

  // UI expansion
  expandedDocs: new Set(),
  expandedGroups: new Set(),

  // Pagination
  page: 1,
  pageSize: 50,
};

function inventoryReducer(state, action) {
  switch (action.type) {
    // Selection persistence (Phase 3): filter/folder/search/view changes
    // PRESERVE state.selected. Selected docs that fall outside the new
    // filter become "hidden" — the bar shows "N selected · M hidden by
    // filter" with a [Show all] affordance. Case changes happen via
    // unmount/remount, which resets state anyway.
    case 'SET_FOLDER':
      return { ...state, currentFolder: action.folder, page: 1 };

    case 'SET_SEARCH':
      return { ...state, searchText: action.text, page: 1 };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: Array.isArray(action.filters) ? action.filters : [],
        page: 1,
      };

    case 'ADD_FILTER': {
      const exists = state.filters.some(
        f => f.axis === action.axis && f.value === action.value
      );
      if (exists) return state;  // idempotent (OR within axis)
      return {
        ...state,
        filters: [...state.filters, { axis: action.axis, value: action.value }],
        page: 1,
      };
    }

    case 'REMOVE_FILTER':
      return {
        ...state,
        filters: state.filters.filter(f =>
          !(f.axis === action.axis && (action.value === undefined || f.value === action.value))
        ),
        page: 1,
      };

    case 'SET_DUPLICATE_FILTER':
      return {
        ...state,
        duplicateFilter: !state.duplicateFilter,
        duplicateHashFilter: null,
        page: 1,
      };

    case 'SET_DUPLICATE_HASH_FILTER':
      return {
        ...state,
        duplicateFilter: false,
        duplicateHashFilter: action.hash,
        page: 1,
      };

    case 'CLEAR_DUPLICATE_HASH_FILTER':
      return { ...state, duplicateHashFilter: null, page: 1 };

    case 'SET_SORT': {
      const sameKey = state.sortKey === action.key;
      return {
        ...state,
        sortKey: action.key,
        sortAsc: sameKey ? !state.sortAsc : true,
      };
    }

    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.mode };

    case 'SET_UNIT': {
      // A row selected in one unit means nothing in the other (a file vs the documents
      // inside it), so the selection and the expanded rows start over.
      const unit = action.unit === UNITS.FILES || action.unit === UNITS.DOCUMENTS ? action.unit : null;
      if (unit === state.unit) return state;
      return { ...state, unit, page: 1, selected: new Set(), expandedDocs: new Set() };
    }

    case 'SET_SHOW_SOURCE_FILES':
      return { ...state, showSourceFiles: !!action.value, page: 1 };

    case 'SET_COLUMNS':
      return { ...state, visibleColumns: resolveVisibleColumns(action.ids) };

    case 'TOGGLE_COLUMN': {
      const set = new Set(state.visibleColumns);
      if (set.has(action.id)) set.delete(action.id);
      else set.add(action.id);
      return { ...state, visibleColumns: resolveVisibleColumns(Array.from(set)) };
    }

    case 'RESET_COLUMNS':
      return { ...state, visibleColumns: DEFAULT_VISIBLE_COLUMNS };

    case 'SET_COLUMN_WIDTH': {
      const next = { ...(state.columnWidths || {}) };
      if (action.width == null) delete next[action.id];
      else next[action.id] = Math.round(action.width);
      return { ...state, columnWidths: next };
    }

    case 'RESET_COLUMN_WIDTHS':
      return { ...state, columnWidths: {} };

    case 'SELECT_DOC': {
      const next = new Set(state.selected);
      if (next.has(action.id)) next.delete(action.id);
      else next.add(action.id);
      return { ...state, selected: next };
    }

    case 'SELECT_ALL': {
      const ids = action.ids || [];
      return { ...state, selected: new Set(ids) };
    }

    case 'CLEAR_SELECTION':
      return { ...state, selected: new Set(), bulkAction: null };

    case 'SET_BULK_ACTION':
      return { ...state, bulkAction: action.action };

    case 'TOGGLE_DOC_EXPAND': {
      const next = new Set(state.expandedDocs);
      if (next.has(action.id)) next.delete(action.id);
      else next.add(action.id);
      return { ...state, expandedDocs: next };
    }

    case 'TOGGLE_GROUP_EXPAND': {
      const next = new Set(state.expandedGroups);
      if (next.has(action.key)) next.delete(action.key);
      else next.add(action.key);
      return { ...state, expandedGroups: next };
    }

    case 'SET_PAGE':
      return {
        ...state,
        page: action.page,
        ...(action.pageSize != null ? { pageSize: action.pageSize } : {}),
      };

    case 'RESET_FILTERS':
      // Clears all chip/search/dupes filters. Selection PRESERVED so
      // the "[Show all]" affordance in the bulk bar can reveal docs
      // that were hidden by the now-cleared filters.
      return {
        ...state,
        searchText: '',
        filters: [],
        duplicateFilter: false,
        duplicateHashFilter: null,
        page: 1,
      };

    default:
      return state;
  }
}

// ── Persistence ──────────────────────────────────────────────

const STORAGE_KEY = 'dc-inventory-prefs';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const prefs = JSON.parse(raw);
    // The retired fourth view mode ("By document") is the Documents unit in Grid view now.
    const legacyDocumentsView = prefs.viewMode === 'documents';
    const viewMode = legacyDocumentsView ? VIEW_MODES.GRID
      : (Object.values(VIEW_MODES).includes(prefs.viewMode) ? prefs.viewMode : VIEW_MODES.LIST);
    const unit = legacyDocumentsView ? UNITS.DOCUMENTS
      : (prefs.unit === UNITS.FILES || prefs.unit === UNITS.DOCUMENTS ? prefs.unit : null);
    return {
      viewMode,
      unit,
      showSourceFiles: prefs.showSourceFiles === true,
      sortKey: prefs.sortKey || SORT_KEYS.FILENAME,
      sortAsc: prefs.sortAsc !== false,
      pageSize: prefs.pageSize || 50,
      visibleColumns: Array.isArray(prefs.visibleColumns)
        ? resolveVisibleColumns(prefs.visibleColumns)
        : DEFAULT_VISIBLE_COLUMNS,
      columnWidths: sanitizeWidths(prefs.columnWidths),
    };
  } catch {
    return {};
  }
}

// Only numbers in a sane range survive a reload (a stale or hand-edited blob must not break the table).
function sanitizeWidths(raw) {
  const out = {};
  if (raw && typeof raw === 'object') {
    for (const [id, w] of Object.entries(raw)) {
      const n = Number(w);
      if (Number.isFinite(n) && n >= 60 && n <= 1200) out[id] = Math.round(n);
    }
  }
  return out;
}

function savePrefs(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      viewMode: state.viewMode,
      unit: state.unit,
      showSourceFiles: state.showSourceFiles,
      sortKey: state.sortKey,
      sortAsc: state.sortAsc,
      pageSize: state.pageSize,
      visibleColumns: state.visibleColumns,
      columnWidths: state.columnWidths || {},
    }));
  } catch {
    // non-critical
  }
}

// ── Provider ─────────────────────────────────────────────────

export function InventoryProvider({ children }) {
  const prefs = loadPrefs();
  const [state, rawDispatch] = useReducer(inventoryReducer, {
    ...initialState,
    ...prefs,
  });

  // Wrap dispatch to persist prefs on relevant changes
  const dispatch = useCallback((action) => {
    rawDispatch(action);
    if (['SET_SORT', 'SET_VIEW_MODE'].includes(action.type)) {
      // We need the *next* state, so we schedule a save
      requestAnimationFrame(() => {
        // Re-read from the reducer indirectly via the provider
        // (simpler: just save on every dispatch — prefs are tiny)
      });
    }
  }, []);

  // Save prefs whenever view/sort changes
  // Using a simpler approach: save on every render where prefs changed
  const prefsKey = `${state.viewMode}|${state.unit}|${state.showSourceFiles}|${state.sortKey}|${state.sortAsc}|${state.pageSize}|${(state.visibleColumns || []).join(',')}`;
  if (typeof window !== 'undefined') {
    const lastKey = window.__dcInventoryPrefsKey;
    if (lastKey !== prefsKey) {
      window.__dcInventoryPrefsKey = prefsKey;
      savePrefs(state);
    }
  }

  return (
    <InventoryContext.Provider value={{ ...state, dispatch }}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
}
